import React, { useEffect, useState } from 'react';
import { Row, Col, Card, CardBody, Modal } from 'reactstrap';
import { get, post, put, del } from 'helpers/api_helper';
import Datatables from 'pages/utils/table/datatable';
import { ManageBankColumns } from './tableColumns';
import { BANK_URL } from 'helpers/url_helper';
import Select from 'react-select';
import { showSuccessAlert, showConfirmAlert } from "pages/utils/alertMessages";
import { AvForm, AvField } from 'availity-reactstrap-validation';
import { useTranslation } from 'react-i18next';

const ManageBankAccount = () => {
    const { t } = useTranslation();
    const [isForm, setIsForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({});
    const [rows, setRows] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, Search: '' });

    const fetchData = async () => {
        setLoading(true);
        const { success, body } = await get(`${BANK_URL}?page=${pagination.page}&limit=${pagination.limit}&search=${pagination.Search}`);
        if (success) {
            setRows(body);
            setLoading(false);
        }
    };

    useEffect(fetchData, [pagination]);

    const handleToggle = () => {
        setIsForm(!isForm);
        setEditMode(false);
        setEditingId(null);
        setFormData({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        const url = editMode ? `${BANK_URL}/${editingId}` : BANK_URL;
        const method = editMode ? put : post;

        const { success, body } = await method(url, formData);

        if (success) {
            showSuccessAlert(editMode ? t('Bank Account updated successfully!') : t('Bank Account created successfully!'));
            setIsForm(false);
            setFormData({});
            setEditMode(false);
            setEditingId(null);
            fetchData();
        }
    };

    const handleInvalidSubmit = () => {
        alert(t("Please enter all required fields correctly."));
    };

    const handleEdit = (row) => {
        setFormData(row);
        setEditingId(row.id);
        setEditMode(true);
        setIsForm(true);
    };

    const handleDelete = async (id, sts) => {
        const action = sts === 1 ? t('De-activate') : t('Activate');

        const { isConfirmed } = await showConfirmAlert(`${action} ${t('Bank Account')}`, `${t('Do you really want to')} ${action} ${t('this Bank Account')}?`,
            `${t('Yes')}, ${action} ${t('it')}!`, `${action}d!`, `${t('Bank Account has been updated successfully.')}`);

        if (isConfirmed) {
            const { success } = await del(`${BANK_URL}/${id}?sts=${sts}`);
            if (success) {
                fetchData();
            }
        }
    };

    return (
        <React.Fragment>
            <Card>
                <CardBody>
                    <Datatables
                        isSearch={true}
                        columns={ManageBankColumns({ "edit": handleEdit, "delete": handleDelete }, pagination)}
                        showTableOnly={true}
                        rowsLength={rows?.totalItems || 0}
                        rows={rows.items || []}
                        keyField={'id'}
                        handleAddButton={handleToggle}
                        title={t("All Clinic Bank & Settlement Accounts")}
                        isAdd={true}
                        isTableHead={true}
                        loading={loading}
                        ssr={setPagination}
                    />
                </CardBody>
            </Card>

            <Modal isOpen={isForm} toggle={handleToggle} size="xl" centered>
                <AvForm onValidSubmit={handleSubmit} onInvalidSubmit={handleInvalidSubmit} model={formData}>
                    <div className="modal-header">
                        <h5 className="modal-title font-weight-bold">
                            {editMode ? t('Update Bank Account') : t('Create New Bank Account')}
                        </h5>
                        <button type="button" className="btn-close" onClick={handleToggle} aria-label="Close" />
                    </div>
                    <div className="modal-body p-4">
                        <Row className="g-3">
                            <Col md={4}>
                                <div className="mb-2">
                                    <AvField
                                        name="bank_name"
                                        label={t("Bank Name *")}
                                        type="text"
                                        placeholder={t("e.g. HDFC Bank, ICICI Bank, SBI")}
                                        value={formData.bank_name || ''}
                                        onChange={handleChange}
                                        required
                                        errorMessage={t("Please enter Bank Name")}
                                    />
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="mb-2">
                                    <AvField
                                        name="ac_no"
                                        label={t("Account Number *")}
                                        type="text"
                                        placeholder={t("Enter Account Number")}
                                        value={formData.ac_no || ''}
                                        onChange={handleChange}
                                        required
                                        errorMessage={t("Please enter Account Number")}
                                    />
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="mb-2">
                                    <AvField
                                        name="ifsc_code"
                                        label={t("IFSC Code *")}
                                        type="text"
                                        placeholder={t("e.g. HDFC0001234")}
                                        value={formData.ifsc_code || ''}
                                        onChange={handleChange}
                                        required
                                        errorMessage={t("Please enter IFSC Code")}
                                    />
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="mb-2">
                                    <AvField
                                        name="branch"
                                        label={t("Branch Name *")}
                                        type="text"
                                        placeholder={t("e.g. MG Road Branch")}
                                        value={formData.branch || ''}
                                        onChange={handleChange}
                                        required
                                        errorMessage={t("Please enter Branch Name")}
                                    />
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="mb-2">
                                    <AvField
                                        name="addrress"
                                        label={t("Bank Address *")}
                                        type="text"
                                        placeholder={t("Enter Bank Address")}
                                        value={formData.addrress || ''}
                                        onChange={handleChange}
                                        required
                                        errorMessage={t("Please enter Bank Address")}
                                    />
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="mb-2">
                                    <label className="form-label font-weight-semibold">{t("Account Type")}</label>
                                    <Select
                                        className="basic-single"
                                        classNamePrefix="select"
                                        name="ac_type"
                                        isClearable={true}
                                        options={[
                                            { value: "saving", label: t("Savings Account") },
                                            { value: "current", label: t("Current Account") }
                                        ]}
                                        value={formData.ac_type ? { value: formData.ac_type, label: formData.ac_type === 'saving' ? t('Savings Account') : (formData.ac_type === 'current' ? t('Current Account') : formData.ac_type) } : null}
                                        onChange={(selected) => setFormData((prev) => ({ ...prev, ac_type: selected ? selected.value : "" }))}
                                        placeholder={t("Select Account Type")}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-danger" onClick={handleToggle}>
                            {t("Cancel")}
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? t('Processing...') : (editMode ? t('Update Bank Account') : t('Save Bank Account'))}
                        </button>
                    </div>
                </AvForm>
            </Modal>
        </React.Fragment>
    );
};

export default ManageBankAccount;
